import { IconName } from "@zenchef/icomoon/dist/aromaticons/types";
import { HStack } from "@zenchef/styled-system/jsx";
import { createContext, PropsWithChildren, useContext, useState } from "react";
import ContextualIcon from "../ContextualIcon/ContextualIcon";
import { Text } from "../typography/Text";
import {
  aromaBucket,
  AromaBucketVariantProps,
} from "@zenchef/styled-system/recipes";
import withTestId from "../../utils/withTestId";

import { Box, VStack, Stack } from "@zenchef/styled-system/jsx";
import {
  Art,
  ArtProps,
  Button,
  ButtonProps,
  EmptyState,
  Icon,
  useWindowSize,
} from "../..";
import { token } from "@zenchef/styled-system/tokens";
import React, { MouseEvent as ReactMouseEvent } from "react";

const getPopupPosition = (index: number, childCount: number) => {
  switch (index) {
    case 0:
      return "top";
    case childCount - 2:
      return "bottom";
    case childCount - 1:
      return "bottom";
    default:
      return "center";
  }
};

export interface AromaBucketProps
  extends AromaBucketVariantProps,
    PropsWithChildren {
  emptySideProps: AromaBucketEmptySideProps;
}

export interface AromaBucketContainerProps
  extends AromaBucketVariantProps,
    PropsWithChildren {}

export interface AromaBucketSectionProps
  extends AromaBucketVariantProps,
    PropsWithChildren {
  iconName: IconName;
  title: string;
  description: string;
  isActive?: boolean;
  onClick?: () => void;
}

export interface AromaBucketEmptySideProps
  extends AromaBucketVariantProps,
    PropsWithChildren {
  title: string;
  description: string;
  buttonText: string;
  buttonProps: ButtonProps;
}

export const BucketContext = createContext<{
  selectedSection: string;
  setSelectedSection: ((sectionName: string) => void) | null;
}>({
  selectedSection: "",
  setSelectedSection: null,
});

interface BucketListProps extends AromaBucketVariantProps {
  artName: ArtProps["name"];
  children: React.ReactNode;
}

interface BucketListWrapProps extends AromaBucketVariantProps {
  children: React.ReactNode;
}

interface BucketListItemProps extends AromaBucketVariantProps {
  title: string;
  icon: IconName;
  to: string;
  LinkComponent: React.ElementType;
}

const AromaBucket = ({
  children,
  emptySideProps,
  ...propsWithVariants
}: AromaBucketProps) => {
  const [variants, props] = aromaBucket.splitVariantProps(propsWithVariants);
  const classes = aromaBucket(variants);
  const [selectedSection, setSelectedSection] = useState("");
  const childCount = React.Children.count(children);
  const enhancedChildren = React.Children.map(children, (child, index) => {
    if (
      React.isValidElement<{
        popupPosition?: AromaBucketVariantProps["popupPosition"];
      }>(child)
    ) {
      return React.cloneElement(child, {
        popupPosition: getPopupPosition(index, childCount),
      });
    }
    return child;
  });

  const handleClick = (e: ReactMouseEvent<HTMLDivElement, MouseEvent>) => {
    if (e.target === e.currentTarget) {
      setSelectedSection("");
    }
  };

  return (
    <div className={classes.wrap} onClick={handleClick} {...props}>
      <VStack className={classes.container} gap="gap.2" alignItems="flex-start">
        <BucketContext.Provider value={{ selectedSection, setSelectedSection }}>
          {enhancedChildren}
        </BucketContext.Provider>
      </VStack>
      {!selectedSection ? <EmptySide {...emptySideProps} /> : null}
    </div>
  );
};

const EmptySide = ({
  title,
  description,
  buttonText,
  buttonProps,
  ...propsWithVariants
}: AromaBucketEmptySideProps) => {
  const [variants, props] = aromaBucket.splitVariantProps(propsWithVariants);
  const classes = aromaBucket(variants);

  return (
    <Box className={classes.emptySide} {...props}>
      <VStack gap="gap.4">
        <EmptyState
          size="small"
          functionName="TaskCompleted"
          heading={title}
          motivation={description}
          actions={
            <Button {...buttonProps} hierarchy="secondary-bold">
              <Icon name="help-life-buoy" />
              <Text>{buttonText}</Text>
            </Button>
          }
        />
      </VStack>
    </Box>
  );
};

const AromaBucketListItem = ({
  title,
  icon,
  to,
  LinkComponent,
  ...propsWithVariants
}: BucketListItemProps) => {
  const [variants, props] = aromaBucket.splitVariantProps(propsWithVariants);
  const classes = aromaBucket(variants);
  return (
    <LinkComponent to={to}>
      <HStack className={classes.bucketListItem} {...props}>
        <HStack width="100%">
          <Icon
            name={icon}
            textStyle="title.m"
            color="content.neutral.base.subtle"
          />
          <Text textStyle="title.m" color="content.neutral.base.subtle">
            {title}
          </Text>
        </HStack>
      </HStack>
    </LinkComponent>
  );
};

const AromaBucketList = ({
  artName,
  children,
  ...propsWithVariants
}: BucketListProps) => {
  const [variants, props] = aromaBucket.splitVariantProps(propsWithVariants);
  const classes = aromaBucket(variants);

  return (
    <Box className={classes.bucketList} {...props}>
      <HStack justifyContent="space-between" width="100%">
        <Stack gap="gap.4" minWidth={240}>
          {children}
        </Stack>
        <Box className={classes.art}>
          <Art name={artName} layout="square" height="152px" width="152px" />
        </Box>
      </HStack>
    </Box>
  );
};

const AromaBucketListWrap = ({
  children,
  ...propsWithVariants
}: BucketListWrapProps) => {
  const [variants, props] = aromaBucket.splitVariantProps(propsWithVariants);
  const classes = aromaBucket(variants);

  return (
    <HStack
      flexDirection="column"
      alignItems="flex-start"
      className={classes.list}
      {...props}
    >
      {children}
    </HStack>
  );
};

const AromaBucketSection = ({
  iconName,
  title,
  description,
  onClick,
  popupPosition,
  children,
  ...propsWithVariants
}: AromaBucketSectionProps) => {
  const [variants, props] = aromaBucket.splitVariantProps(propsWithVariants);
  const classes = aromaBucket(variants);
  const [isHovered, setIsHovered] = useState(false);
  const { selectedSection, setSelectedSection } = useContext(BucketContext);
  const isActive = selectedSection === title;
  const { width: windowWidth } = useWindowSize();
  const isMobileScreen = windowWidth <= parseInt(token("breakpoints.sm"), 10);

  return (
    <HStack
      flexDirection="column"
      position="relative"
      width="100%"
      className={classes.sectionWrap}
      {...props}
    >
      <HStack
        className={classes.section}
        gap="gap.4"
        borderRadius="l"
        alignItems="flex-start"
        width="100%"
        onMouseEnter={() => {
          setIsHovered(true);
          setSelectedSection && setSelectedSection(title);
        }}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsHovered(true)}
        onBlur={() => setIsHovered(false)}
        onClick={() => {
          if (isMobileScreen) {
            return;
          }
          onClick?.();
        }}
        tabIndex={0}
      >
        <ContextualIcon
          name={iconName}
          colorTheme="brand"
          hierarchy={isHovered ? "bold" : "subtle"}
        />
        <HStack flexDirection="column" gap="gap.2" alignItems="flex-start">
          <Text textStyle="title.m">{title}</Text>
          <Text
            textStyle="paragraph.m.regular"
            color="content.neutral.base.subtle"
          >
            {description}
          </Text>
        </HStack>
      </HStack>
      {isActive ? (
        <AromaBucketListWrap popupPosition={popupPosition}>
          {children}
        </AromaBucketListWrap>
      ) : null}
    </HStack>
  );
};

AromaBucket.Section = withTestId("nav")(AromaBucketSection);
AromaBucket.List = AromaBucketList;
AromaBucket.Item = withTestId("nav")(AromaBucketListItem);

export default AromaBucket;
